/**
 * The `on_click` (action-chain) field with the Appsmith **visual ⇄ JS** toggle
 * (ADR-0044 U-track — the owner's "edit the handler as code, and back" model).
 *
 *   - **JS off** — the structured `ActionChainEditor` (cards + the categorised
 *     "+" menu).
 *   - **JS on** — the WHOLE chain as editable JS: `navigate("/x"); toast("hi");`
 *     (`lib/actionsJs.actionsToJs`). Stored as a single `run_js_inline` action
 *     whose `code` the runtime executes against the verb SDK.
 *
 * The conversion is **bidirectional best-effort**: toggling on serialises the
 * chain to JS; toggling back PARSES it (`jsToActions`) — if every statement maps
 * to a verb the visual editor returns; if the JS has constructs we can't model
 * (control-flow, an unknown call) it stays JS (the toggle-back is refused with a
 * hint, NOT a silent drop). A chain that ALREADY contains advanced logic
 * (`if`/`try`/continuations/`confirm`/`run_js`) isn't round-trippable, so the JS
 * toggle is hidden — it's edited in the visual editor only.
 *
 * Initial mode is DERIVED: a chain stored as one `run_js_inline` opens in JS.
 */
import * as React from "react";

import { CodeEditor } from "@/components/canonical/CodeEditor";
import { actionsToJs, isConvertibleChain, isInlineJs, jsToActions } from "@/lib/actionsJs";
import type { PageAction } from "@/lib/pageActions";

import { JsBadge } from "./BindableField";
import { ActionChainEditor, type ActionChainEditorProps } from "./ActionChainEditor";

const fieldLabel = "text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70";
const ID_RE = /^[A-Za-z_$][\w$]*$/;

/** The verb SDK declarations (the action vocabulary as bare callables). The
 *  `runQuery` arg is narrowed to the declared query NAMES so it autocompletes.
 *  `$page`/`$queries`/`$global`/`$item`/JS Objects are NOT declared here — the
 *  page-bindings `.d.ts` (`buildPageBindingsDts`) owns them, and a DUPLICATE
 *  `declare const` makes TypeScript reject the WHOLE ambient lib → no
 *  completions at all (the bug). `$json`/`$items` aren't in that dts, so a
 *  minimal fallback is appended ONLY when no page-bindings dts is supplied. */
function verbDts(queryNames: string[], hasAmbient: boolean): string {
  const names = queryNames.filter((n) => typeof n === "string" && n);
  const union = names.length ? names.map((n) => JSON.stringify(n)).join(" | ") : "string";
  const fallback = hasAmbient
    ? ""
    : "\ndeclare const $page: any; declare const $queries: any; declare const $global: any; declare const $item: any; declare const $input: any;";
  return `
declare function navigate(to: any): void;
declare function toast(message: any): void;
declare function refresh(): void;
declare function confirm(message: any): boolean;
declare function setVar(name: string, value: any): void;
/** Run a declared page query / operation. */
declare function runQuery(name: ${union}): Promise<void>;
declare function runWorkflow(id: string, values?: any): Promise<any>;
/** Render + download a granted report as PDF (ADR-0060 S5). */
declare function printReport(report: string, params?: any): Promise<void>;
declare function createRow(table: string, values?: any): Promise<any>;
declare function updateRow(table: string, row: any, values?: any): Promise<any>;
declare function deleteRow(table: string, row: any): Promise<any>;
declare function openDrawer(page: string, vars?: any): void;
declare function openOverlay(page: string, mode?: "modal" | "drawer"): void;
declare function closeOverlay(result?: any): void;
/** Open an in-page modal/drawer component by its node id (ADR-0045). */
declare function openModal(target: string): void;
/** Close an in-page modal/drawer component by its node id (ADR-0045). */
declare function closeModal(target: string): void;
declare function storeValue(key: string, value: any): void;
declare function removeValue(key: string): void;
declare function clearStore(): void;
declare const global: { set(k: string, v: any): void; remove(k: string): void; clear(): void };
/** The triggering event in a per-widget handler (ADR-0045 F4.2): \`value\` on an
 *  input's on_change/on_blur, \`row\` on a data_list's on_row_select. */
declare const $event: { value?: any; row?: any };
declare const $json: any; declare const $items: any[];${fallback}`;
}

/** `<Query>.run()` objects (the Appsmith query shape) — one per valid-identifier
 *  query name, EXCLUDING any that collide with a JS Object name (those are
 *  declared by the page-bindings dts) so we never double-declare. */
function queryRunDts(queryNames: string[], jsFunctions: string[]): string {
  const jsObjectNames = new Set(
    jsFunctions.map((r) => r.split(".")[0]).filter((n) => ID_RE.test(n)),
  );
  return queryNames
    .filter((n) => ID_RE.test(n) && !jsObjectNames.has(n))
    .map(
      (n) =>
        `declare const ${n}: { /** Run this query. */ run(): Promise<any>; data: any; rows: any[] };`,
    )
    .join("\n");
}

/** The action-SDK ambient lib (verbs + `<Query>.run()`). Goes in its OWN Monaco
 *  extra-lib slot (`sdkAmbientTypes`) so it never clobbers / is clobbered by the
 *  shared page-bindings lib (`jsAmbientTypes` = `ambientDts`). The two MUST be
 *  disjoint — `verbDts` skips `$page`/`$queries`/… when the page bindings are
 *  present, `queryRunDts` skips a name colliding with a JS Object. */
function buildActionSdkDts(queryNames: string[], jsFunctions: string[], hasAmbient: boolean): string {
  return [verbDts(queryNames, hasAmbient), queryRunDts(queryNames, jsFunctions)].filter(Boolean).join("\n");
}

/** The current code shown in JS mode: a stored `run_js_inline`'s `code`, else a
 *  serialisation of the structured chain. */
function codeFor(value: unknown): string {
  if (isInlineJs(value)) return String((value as PageAction[])[0].code ?? "");
  return actionsToJs(value);
}

export type ActionFieldProps = Omit<ActionChainEditorProps, "value"> & {
  label: string;
  value: unknown;
  /** Live page-binding `.d.ts` (`$queries`/`$page`/JS Objects, column-aware) from
   *  `lib/pagePreview.buildPageBindingsDts` — fed to the inline-JS IntelliSense. */
  ambientDts?: string;
};

export function ActionField({ label, value, onChange, ambientDts, ...rest }: ActionFieldProps) {
  const queryNames = rest.queryNames;
  const jsFunctions = rest.jsFunctions;
  // The verb SDK + `<Query>.run()` live in a DEDICATED Monaco lib slot
  // (`sdkAmbientTypes`); the page bindings ride the shared `jsAmbientTypes` slot
  // (same as the value-binding fields). Keeping them in SEPARATE slots is what
  // stops a sibling editor's page-bindings lib from clobbering the verbs.
  const actionSdkDts = React.useMemo(
    () => buildActionSdkDts(queryNames ?? [], jsFunctions ?? [], !!ambientDts),
    [queryNames, jsFunctions, ambientDts],
  );
  const [jsMode, setJsMode] = React.useState(() => isInlineJs(value));
  const [parseError, setParseError] = React.useState(false);

  // The JS toggle is available whenever the chain serialises losslessly to JS —
  // leaves, `run_js` (a JS-Object call), and `if`/`try` (real JS blocks). It's
  // hidden ONLY for the genuinely non-representable cases (a per-action
  // `confirm` or an on-success/on-failure CONTINUATION), which stay visual-only.
  const convertible = isInlineJs(value) || isConvertibleChain(value);

  const toggle = () => {
    setParseError(false);
    if (jsMode) {
      // Leaving JS → parse the code back to a structured chain. Refuse (stay JS)
      // when it can't be cleanly modelled — never silently drop the user's code.
      const { actions, lossy } = jsToActions(codeFor(value), { queryNames });
      if (lossy) {
        setParseError(true);
        return;
      }
      onChange(actions);
      setJsMode(false);
    } else {
      // Entering JS → serialise the current chain to inline JS.
      onChange([{ type: "run_js_inline", code: actionsToJs(value) }]);
      setJsMode(true);
    }
  };

  return (
    <div className="flex flex-col gap-1.5 border-b border-border/50 py-2 last:border-0">
      <span className={`flex items-center gap-1.5 ${fieldLabel}`}>
        {label}
        {convertible && <JsBadge active={jsMode} onToggle={toggle} />}
      </span>

      {jsMode ? (
        <div className="flex flex-col gap-1.5">
          <div className="h-40 w-full">
            <CodeEditor
              value={codeFor(value)}
              onChange={(code) => onChange([{ type: "run_js_inline", code }])}
              language="javascript"
              height="100%"
              inline
              jsAmbientTypes={ambientDts}
              sdkAmbientTypes={actionSdkDts}
            />
          </div>
          {parseError && (
            <p className="text-[11px] leading-snug text-danger/80">
              Questo JS non è convertibile in blocchi visuali (usa logica avanzata
              o una funzione non riconosciuta). Restano disponibili in JS.
            </p>
          )}
          <p className="text-[11px] leading-snug text-muted-foreground/80">
            Le azioni come codice: <code className="font-mono">navigate("/x"); toast("ok");</code>.
            Torna ai blocchi con il badge <strong>JS</strong> quando il codice è
            convertibile.
          </p>
        </div>
      ) : (
        <>
          <ActionChainEditor value={value} onChange={onChange} {...rest} />
          {!convertible && (
            <p className="text-[11px] leading-snug text-muted-foreground/70">
              Catena con logica avanzata — modificabile solo in modalità visuale.
            </p>
          )}
        </>
      )}
    </div>
  );
}
